import React, { useEffect } from 'react';
import { useSeccionForm } from '../hooks/useSeccionForm';
import { FormInput, FormSelect } from '../components/form/FormFields';

// ============================================================
// SeccionFormPage – Registro / Modificación de Sección
// ============================================================

interface Props {
  idSeccion?: number;
  onSuccess?: () => void;
  onCancel?:  () => void;
}

const SpinnerIcon = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const SeccionFormPage: React.FC<Props> = ({ idSeccion, onSuccess = () => {}, onCancel = () => {} }) => {
  const isEditing = idSeccion !== undefined;

  const {
    form, errors, submitting, submitError, loadingInit, grados,
    handleChange, handleSubmit, loadSeccion,
  } = useSeccionForm(idSeccion ?? null, onSuccess);

  useEffect(() => { loadSeccion(); }, [loadSeccion]);

  if (loadingInit) {
    return <div className="flex justify-center items-center h-64"><SpinnerIcon /></div>;
  }

  return (
    <div className="max-w-md mx-auto">

      {/* Cabecera */}
      <div className="mb-6 flex items-center gap-3">
        <button onClick={onCancel}
          className="p-2 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100 transition-colors">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"/>
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-800">
            {isEditing ? 'Modificar Sección' : 'Registrar Sección'}
          </h1>
          <p className="text-slate-400 text-sm">
            {isEditing ? 'Actualiza los datos de la sección' : 'Completa los datos para crear una nueva sección'}
          </p>
        </div>
      </div>

      {/* Tarjeta */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
          <h2 className="text-white font-bold">Datos de la Sección</h2>
          <p className="text-blue-200 text-xs mt-0.5">Identificador de la sección escolar</p>
        </div>

        <div className="p-6 space-y-4">

          <FormSelect
            label="Grado"
            name="gradoId"
            value={form.gradoId}
            onChange={handleChange}
            error={errors.gradoId}
            required
            placeholder="Seleccionar grado"
            options={grados.map(g => ({ value: g.idGrado, label: `${g.nombre} – ${g.nivel}` }))}
          />

          <FormInput
            label="Nombre de la Sección"
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
            error={errors.nombre}
            required
            placeholder="Ej: A, B, C"
            maxLength={10}
          />

          <FormInput
            label="Capacidad máxima"
            name="capacidad"
            type="number"
            value={form.capacidad}
            onChange={handleChange}
            error={errors.capacidad}
            required
            placeholder="40"
            hint="Número máximo de alumnos por sección (afecta el % de capacidad del dashboard)"
          />

          {/* Toggle activo */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div>
              <p className="text-sm font-semibold text-slate-700">Estado de la Sección</p>
              <p className="text-xs text-slate-400 mt-0.5">
                {form.activo ? 'La sección está habilitada' : 'La sección está deshabilitada'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleChange({
                target: { name: 'activo', value: '', type: 'checkbox', checked: !form.activo },
              } as React.ChangeEvent<HTMLInputElement>)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                transition-colors duration-200 ease-in-out focus:outline-none
                ${form.activo ? 'bg-blue-600' : 'bg-slate-300'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow
                ring-0 transition duration-200 ease-in-out
                ${form.activo ? 'translate-x-5' : 'translate-x-0'}`}
              />
            </button>
          </div>

          {submitError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
              </svg>
              <span className="text-red-600 text-sm">{submitError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <button onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold
                       bg-green-600 hover:bg-green-700 text-white transition-colors shadow-sm disabled:opacity-60">
            {submitting ? <SpinnerIcon /> : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
              </svg>
            )}
            {isEditing ? 'Guardar Cambios' : 'Registrar Sección'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeccionFormPage;
